import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useEffect, useRef, useState } from 'react';
import { faArrowRight, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Branch } from '@medprev-web-app/api-graphql-client';

import { BranchAvatar } from './branch-avatar.component';
import { BranchProfileModal } from './branch-profile-modal.component';

import { AnalyticsBranchPageEnum } from '@/app/(main)/(pages)/_enums/analytics-branch-page.enum';
import { Card } from '@/components/ui/card.component';
import { Link } from '@/components/ui/link/link.component';
import { cn } from '@/utils/cn-tw-merge';
import {
  formatAddress,
  FormatAddressParams,
  formatGoogleMapsAddress,
  formatSimpleAddress,
} from '@/utils/formatters/format-address';
import { formatBranchName } from '@/utils/formatters/branch';
import { generateWhatsAppUrl } from '@/utils/formatters/phonenumber';
import { branchDefaultWhatsappMessage } from '@/utils/consts/cta-whatsapp-messages.consts';
import { googleMapsBaseUrl } from '@/utils/consts/google-maps.consts';
import { Icon } from '@/components/ui/icon.component';
import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useSmartPrefetch } from '@/hooks/use-smart-prefetch.hook';

interface BranchesListItemProps extends React.HTMLAttributes<HTMLDivElement> {
  citySlug: string;
  branch: Branch;
  pageFrom?: AnalyticsBranchPageEnum;
}

export const BranchesListItem = ({
  citySlug,
  branch,
  className = '',
  pageFrom,
}: BranchesListItemProps) => {
  const formattedAddress = formatGoogleMapsAddress(
    branch.address as FormatAddressParams,
  );
  const branchName = formatBranchName(branch.name as string);
  const branchAddress = formatAddress(branch.address as FormatAddressParams);
  const branchSimpleAddress = formatSimpleAddress(
    branch.address as FormatAddressParams,
  );
  const branchUrl = `/cidades/${citySlug}/${branch.slug}`;
  const branchWhatsapp = branch?.whatsapp as string;

  const [expanded, setExpanded] = useState(false);
  const [height, setHeight] = useState<number>(0);
  const [openModal, setOpenModal] = useState(false);
  const addressRef = useRef<HTMLDivElement>(null);
  const analytics = getAnalyticsPlugin();
  const smartPrefetch = useSmartPrefetch({
    href: branchUrl,
  });

  const toggleExpand = (): void => {
    if (!expanded) {
      analytics.track(AnalyticsEnum.CLICK_EXPAND_BRANCH_ADDRESS_CITY_PAGE, {
        unidade_nome: branchName,
        pagina_acesso: pageFrom,
      });
    }
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (addressRef.current) {
      if (expanded) {
        setHeight(addressRef.current.scrollHeight);
      } else {
        setHeight(0);
      }
    }
  }, [expanded]);

  const openBranchProfileModalByName = (): void => {
    analytics.track(AnalyticsEnum.CLICK_BRANCH_NAME, {
      unidade_nome: branchName,
      pagina_acesso: pageFrom,
    });

    setOpenModal(true);
  };

  const openBranchProfileModalByAvatar = (): void => {
    analytics.track(AnalyticsEnum.CLICK_BRANCH_AVATAR, {
      unidade_nome: branchName,
      pagina_acesso: pageFrom,
    });

    setOpenModal(true);
  };

  const MapLink = (
    <Link
      prefetch={false}
      data-testid={DataTestIdEnum.CITY_BRANCH_LIST_SEE_ON_MAP_BUTTON}
      target="_blank"
      href={`${googleMapsBaseUrl}${`Medprev ${formattedAddress}`}`}
      className="text-green-500 leading-6 font-semibold hover:underline"
      onClick={() => {
        analytics.track(AnalyticsEnum.CLICK_OPEN_MAP_BRANCH_CARD, {
          unidade_nome: branchName,
          pagina_acesso: pageFrom,
        });
      }}
    >
      Abrir no mapa
      {' '}
      <Icon icon={faArrowRight} className="ml-1 text-green-500" />
    </Link>
  );

  return (
    <>
      <Card
        data-testid={DataTestIdEnum.CITY_BRANCH_LIST_ITEM}
        className={cn(className, [
          'w-full py-5 px-6 sm:px-16 md:p-6 rounded-none md:rounded-xl',
          'border-x-0 border-b border-t-0 first:border-t border-gray-300',
          'md:border',
        ])}
      >
        <div className="flex flex-col xl:flex-row gap-4 justify-between h-full">
          <div className="w-full xl:w-2/3">
            <div className="flex flex-row gap-4">
              <div
                className="min-w-22 min-h-22 cursor-pointer"
                onClick={openBranchProfileModalByAvatar}
              >
                <BranchAvatar branch={branch} />
              </div>
              <div className="flex flex-col">
                <Typography
                  variant={TypographyVariant.MEDIUM_SUBTITLE}
                  data-testid={DataTestIdEnum.CITY_BRANCH_LIST_TITLE}
                  className="text-green-500 font-semibold line-clamp-3 mb-3 cursor-pointer"
                  onClick={openBranchProfileModalByName}
                >
                  {branchName}
                </Typography>
                <div className="hidden xl:block">
                  <Typography
                    data-testid={DataTestIdEnum.CITY_BRANCH_LIST_ADDRESS}
                    className="mb-1"
                  >
                    {branchAddress}
                  </Typography>
                  {MapLink}
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:block xl:hidden">
            <Typography
              data-testid={DataTestIdEnum.CITY_BRANCH_LIST_ADDRESS}
              className="mb-1"
            >
              {branchAddress}
            </Typography>
            {MapLink}
          </div>

          <div className="block md:hidden">
            <div className="flex justify-between w-full">
              <div className="flex">
                <Typography>{branchSimpleAddress}</Typography>
              </div>
              <div
                data-testid={DataTestIdEnum.CITY_BRANCH_LIST_TOGGLE_BUTTON}
                onClick={toggleExpand}
                className="transition-colors duration-300 ease-in-out text-center bg-gray-200 hover:bg-gray-300 w-6 h-6 rounded-full content-center hover:cursor-pointer"
              >
                <Icon
                  icon={faChevronDown}
                  className={`transition-transform duration-300 ease-in-out text-gray-700 ${expanded ? 'rotate-180' : ''}`}
                />
              </div>
            </div>
            <div
              style={{ height: height ? `${height}px` : '0' }}
              ref={addressRef}
              className="overflow-hidden transition-all duration-500 ease-in-out"
            >
              <Typography className="text-gray-700 mt-2 mb-1">
                {branchAddress}
              </Typography>
              {MapLink}
            </div>
          </div>

          <div className="flex flex-col md:flex-row xl:flex-col gap-2 w-full xl:w-1/3">
            {branchWhatsapp && (
              <Link
                prefetch={false}
                target="_blank"
                href={generateWhatsAppUrl(
                  branchWhatsapp,
                  branchDefaultWhatsappMessage,
                )}
                className="w-full"
                data-analytics-event={AnalyticsEnum.CLICK_SCHEDULE_BRANCH}
                data-analytics-properties={JSON.stringify({
                  unidade_nome: branchName,
                  pagina_acesso: pageFrom,
                })}
              >
                <Button
                  data-testid={DataTestIdEnum.CITY_BRANCH_LIST_WHATSAPP_BUTTON}
                  className="w-full h-12"
                  startIcon={faWhatsapp}
                  startIconClassName="max-w-5 max-h-5"
                >
                  Quero agendar
                </Button>
              </Link>
            )}

            <Link
              prefetch={false}
              href={branchUrl}
              className="w-full"
              onMouseEnter={smartPrefetch.onMouseEnter}
              onTouchStart={smartPrefetch.onTouchStart}
            >
              <Button
                variant="text"
                data-testid={DataTestIdEnum.CITY_BRANCH_LIST_VIEW_BRANCH_BUTTON}
                className="w-full h-12"
                startIcon={faArrowRight}
                startIconClassName="max-w-5 max-h-5"
                onClick={() => {
                  analytics.track(AnalyticsEnum.CLICK_VIEW_BRANCH, {
                    unidade_nome: branchName,
                    pagina_acesso: pageFrom,
                  });
                }}
              >
                Ver unidade
              </Button>
            </Link>
          </div>
        </div>
      </Card>

      <BranchProfileModal
        open={openModal}
        onOpenChange={setOpenModal}
        branch={branch}
      />
    </>
  );
};
